import React, { useEffect, useState } from 'react';
import {
  Table, Button, Card, Tag, Select,
  message, DatePicker, Popconfirm, Modal, Form, Input, InputNumber
} from 'antd';
import { DeleteOutlined, PlusOutlined, EditOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { getPayments, deletePayment } from '../api/payments';
import { getBranches } from '../api/branches';
import useAuthStore from '../store/authStore';
import { formatStudentLabel } from '../utils/studentLabel';
import usePersistedPageSize from '../hooks/usePersistedPageSize';
import useServerSort from '../hooks/useServerSort';
import api from '../api/axios';
import { apiErrorMessage } from '../utils/apiError';
import { OgrenciVeliPortalRozeti } from '../components/common/VeliPortalRozeti';

const { Option } = Select;
const { RangePicker } = DatePicker;

// DIKKAT - iki ayri kavram, isimleri benziyor:
// MANAGER_ROLES  = sube seciciyi gorenler. Yalnizca super adminler; sube
//                  muduru zaten kendi subesine kilitli, secmesi anlamsiz.
// ODEME_YETKILI  = bu sayfanin ASIL sahipleri. Disinda kalan biri (ornegin
//                  kendisine Yetkiler'den payments.view.all verilmis bir
//                  ogretmen) sayfayi acabilir ama kisitli acar (asagi bak).
const MANAGER_ROLES = ['developer_admin', 'super_admin'];
const ODEME_YETKILI = ['developer_admin', 'super_admin', 'branch_manager'];

const Payments = () => {
  const { user } = useAuthStore();

  // Bu sayfa normalde yalnizca yoneticilere acik. Bir ogretmene Kullanicilar >
  // Yetkiler'den payments.view.all verildiginde sayfa aciliyor - ama ona
  // verilen is "bugun kim ne odedi" takibi, gecmis tum tahsilati gormek
  // degil. Bu yuzden yonetici olmayan biri sayfayi acinca tarih araligi
  // BUGUN'e sabitleniyor ve degistirilemiyor, sube de kendi subesi olarak
  // kilitli geliyor.
  //
  // ONEMLI: bu bir ONLEM, yetki kontrolu DEGIL. Backend tarafinda tarih
  // zorunlulugu yok; ucu dogrudan cagiran biri gecmisi yine gorebilir
  // (yetkiyi zaten almis durumda). Kullanicinin bilincli tercihi - amac
  // gunluk kullanimda gereksiz veriyi ekrana getirmemek.
  // Bkz. frontend/CLAUDE.md "UI'da gizlemek yetki degildir".
  const gunuKisitli = !ODEME_YETKILI.includes(user?.role);

  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [persistedPageSize, setPersistedPageSize] = usePersistedPageSize('payments', 15);
  const [pagination, setPagination] = useState({ current: 1, pageSize: persistedPageSize, total: 0 });
  const { sortField, sortOrder, applySorter } = useServerSort();
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [dateRange, setDateRange] = useState(gunuKisitli ? [dayjs(), dayjs()] : null);
  const [branchFilter, setBranchFilter] = useState(null);
  const [branches, setBranches] = useState([]);
  const [studentFilter, setStudentFilter] = useState(null);
  const [studentFilterOptions, setStudentFilterOptions] = useState([]);
  const [studentFilterLoading, setStudentFilterLoading] = useState(false);
  const navigate = useNavigate();

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addForm] = Form.useForm();
  const [studentSearch, setStudentSearch] = useState('');
  const [studentOptions, setStudentOptions] = useState([]);
  const [studentSearchLoading, setStudentSearchLoading] = useState(false);
  const [addLoading, setAddLoading] = useState(false);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editForm] = Form.useForm();
  const [editingPayment, setEditingPayment] = useState(null);
  const [editLoading, setEditLoading] = useState(false);

  useEffect(() => {
    if (MANAGER_ROLES.includes(user?.role)) {
      getBranches({ per_page: 100 }).then(res => setBranches(res.data.data || [])).catch(() => {});
    }
  }, [user?.role]);

  const fetchPayments = async (page = 1, perPage = pagination.pageSize, sortBy = sortField, sortDir = sortOrder ? (sortOrder === 'descend' ? 'desc' : 'asc') : null) => {
    setLoading(true);
    try {
      const params = {
        page,
        per_page: perPage,
        payment_method: paymentMethod,
        date_from: dateRange?.[0]?.format('YYYY-MM-DD'),
        date_to: dateRange?.[1]?.format('YYYY-MM-DD'),
        branch_id: branchFilter || undefined,
        student_id: studentFilter || undefined,
        sort_by: sortBy || undefined,
        sort_direction: sortDir || undefined,
      };
      const res = await getPayments(params);
      setPayments(res.data.data);
      setPagination({
        current: res.data.current_page,
        pageSize: res.data.per_page,
        total: res.data.total,
      });
    } catch (error) {
      message.error('Ödemeler yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  const searchStudentsForFilter = async (value) => {
    if (!value || value.length < 2) { setStudentFilterOptions([]); return; }
    setStudentFilterLoading(true);
    try {
      const res = await api.get('/students', { params: { search: value, per_page: 20 } });
      setStudentFilterOptions(res.data.data || []);
    } catch {
      setStudentFilterOptions([]);
    } finally {
      setStudentFilterLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, [paymentMethod, dateRange, branchFilter, studentFilter]);

  const searchStudents = async (value) => {
    if (!value || value.length < 2) { setStudentOptions([]); return; }
    setStudentSearchLoading(true);
    try {
      const res = await api.get('/students', { params: { search: value, per_page: 20 } });
      setStudentOptions(res.data.data || []);
    } catch {
      setStudentOptions([]);
    } finally {
      setStudentSearchLoading(false);
    }
  };

  const handleAddPayment = async (values) => {
    if (values.amount <= 0) { message.error("Tutar 0'dan büyük olmalıdır."); return; }
    setAddLoading(true);
    try {
      await api.post(`/students/${values.student_id}/payments`, {
        amount: values.amount,
        payment_method: values.payment_method,
        paid_at: dayjs().format('YYYY-MM-DD HH:mm:ss'),
        notes: values.notes,
      });
      message.success('Ödeme kaydedildi.');
      setAddModalOpen(false);
      addForm.resetFields();
      setStudentOptions([]);
      fetchPayments();
    } catch (err) {
      message.error(apiErrorMessage(err, 'Ödeme kaydedilemedi.'));
    } finally {
      setAddLoading(false);
    }
  };

  const openEditModal = (record) => {
    setEditingPayment(record);
    editForm.setFieldsValue({
      amount: parseFloat(record.amount),
      payment_method: record.payment_method,
      paid_at: dayjs(record.paid_at),
      notes: record.notes || '',
    });
    setEditModalOpen(true);
  };

  const handleEditPayment = async (values) => {
    setEditLoading(true);
    try {
      await api.put(`/payments/${editingPayment.id}`, {
        amount: values.amount,
        payment_method: values.payment_method,
        paid_at: values.paid_at.format('YYYY-MM-DD HH:mm:ss'),
        notes: values.notes || null,
      });
      message.success('Ödeme güncellendi.');
      setEditModalOpen(false);
      setEditingPayment(null);
      fetchPayments(pagination.current, pagination.pageSize);
    } catch (err) {
      message.error(apiErrorMessage(err, 'Ödeme güncellenemedi.'));
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deletePayment(id);
      message.success('Ödeme iptal edildi, borçlar yeniden açıldı.');
      fetchPayments();
    } catch (error) {
      message.error('Ödeme iptal edilemedi.');
    }
  };

  const columns = [
    {
      title: 'Tarih',
      dataIndex: 'paid_at',
      key: 'paid_at',
      sorter: true,
      sortOrder: sortField === 'paid_at' ? sortOrder : null,
      render: (v) => dayjs(v).format('DD.MM.YYYY HH:mm'),
    },
    {
      title: 'Öğrenci',
      dataIndex: 'student',
      key: 'student',
      render: (student, record) => student ? (
        <span className="inline-flex items-center gap-2">
          <span
            className="text-blue-500 hover:underline cursor-pointer"
            onClick={() => navigate(`/students/${student.id}`)}
          >
            {student.name} {student.surname}
          </span>
          {/* Ödeme masasındaki soru: bu veliye "uygulamayı kullanın" demek
              gerekiyor mu? Rozet öğrencinin VELİLERİNİ özetliyor; kural
              backend'deki VeliPortalDurumu ile aynı. */}
          <OgrenciVeliPortalRozeti veliPortal={record.veli_portal} />
        </span>
      ) : '—',
    },
    {
      title: 'Şube',
      dataIndex: 'student',
      key: 'branch',
      sorter: true,
      sortOrder: sortField === 'branch' ? sortOrder : null,
      render: (student) => student?.branch?.name || '—',
    },
    {
      title: 'Tutar',
      dataIndex: 'amount',
      key: 'amount',
      sorter: true,
      sortOrder: sortField === 'amount' ? sortOrder : null,
      render: (v) => `${v} ₺`,
    },
    {
      title: 'Yöntem',
      dataIndex: 'payment_method',
      key: 'payment_method',
      sorter: true,
      sortOrder: sortField === 'payment_method' ? sortOrder : null,
      render: (v) => (
        <Tag color={v === 'cash' ? 'green' : 'blue'}>
          {v === 'cash' ? 'Nakit' : 'Havale/EFT'}
        </Tag>
      ),
    },
    {
      title: 'Alan',
      dataIndex: 'received_by',
      key: 'received_by',
      render: (v) => v ? (
        <Button type="link" style={{ padding: 0, height: 'auto' }} onClick={() => navigate(`/users/${v.id}`)}>
          {v.name} {v.surname}
        </Button>
      ) : '—',
    },
    {
      title: 'Notlar',
      dataIndex: 'notes',
      key: 'notes',
      render: (v) => v || '—',
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_, record) => (
        <div className="flex gap-2">
          <Button icon={<EditOutlined />} size="small" onClick={() => openEditModal(record)}>
            Düzenle
          </Button>
          <Popconfirm
            title="Ödemeyi iptal etmek istediğinize emin misiniz? Borçlar yeniden açılacak."
            onConfirm={() => handleDelete(record.id)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button icon={<DeleteOutlined />} danger size="small">
              İptal Et
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Ödeme Yönetimi</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddModalOpen(true)}>
          Ödeme Ekle
        </Button>
      </div>

      <Card>
        <div className="flex gap-3 mb-4 flex-wrap">
          <Select
            showSearch
            allowClear
            placeholder="Öğrenci ara..."
            filterOption={false}
            onSearch={searchStudentsForFilter}
            loading={studentFilterLoading}
            style={{ width: 220 }}
            value={studentFilter}
            onChange={v => setStudentFilter(v ?? null)}
            notFoundContent={studentFilterLoading ? 'Aranıyor...' : 'En az 2 karakter girin'}
          >
            {studentFilterOptions.map(s => (
              <Option key={s.id} value={s.id}>{formatStudentLabel(s)}</Option>
            ))}
          </Select>
          {MANAGER_ROLES.includes(user?.role) && (
            <Select
              placeholder="Şube"
              allowClear
              style={{ width: 160 }}
              value={branchFilter}
              onChange={v => setBranchFilter(v ?? null)}
            >
              {branches.map(b => <Option key={b.id} value={b.id}>{b.name}</Option>)}
            </Select>
          )}
          {/* Kisitli kullanicida sube secilebilir degil, kendi subesi. Gizlemek
              yerine kilitli gosteriyoruz - "neden sube filtresi yok" diye
              sormasin, hangi subenin verisine baktigini gorsun. Kapsami
              backend zaten authorizedBranchIds() ile daraltiyor. */}
          {gunuKisitli && (
            <Select
              disabled
              style={{ width: 160 }}
              value={user?.branch_id ?? null}
              options={[{ value: user?.branch_id ?? null, label: user?.branch_name || 'Şubem' }]}
            />
          )}
          <Select
            placeholder="Ödeme yöntemi"
            allowClear
            style={{ width: 160 }}
            onChange={(value) => setPaymentMethod(value)}
          >
            <Option value="cash">Nakit</Option>
            <Option value="transfer">Havale/EFT</Option>
            <Option value="credit_card">Kredi Kartı</Option>
            <Option value="other">Diğer</Option>
          </Select>
          {/* Kisitli kullanicida bugune sabit ve degistirilemez.
              `value` veriliyor ki state ile senkron kalsin - yoksa secici bos
              gorunur ama liste bugune filtrelenmis olur, kullanici sebebini
              anlamaz. */}
          <RangePicker
            format="DD.MM.YYYY"
            value={dateRange}
            disabled={gunuKisitli}
            onChange={(dates) => setDateRange(dates)}
            placeholder={['Başlangıç', 'Bitiş']}
          />
        </div>

        <Table
          columns={columns}
          dataSource={payments}
          rowKey="id"
          loading={loading}
          onChange={(paginationInfo, _filters, sorter) => {
            const { sortField: sField, sortDirection: sDir } = applySorter(sorter);
            setPersistedPageSize(paginationInfo.pageSize);
            fetchPayments(paginationInfo.current, paginationInfo.pageSize, sField, sDir);
          }}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            pageSizeOptions: ['15', '30', '50', '100'],
            showTotal: (total) => `Toplam ${total} ödeme`,
          }}
          summary={(pageData) => {
            const total = pageData.reduce((sum, row) => sum + parseFloat(row.amount || 0), 0);
            return (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={3}>
                  <strong>Bu Sayfanın Toplamı</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3}>
                  <strong className="text-green-600">{total.toFixed(2)} ₺</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4} colSpan={4} />
              </Table.Summary.Row>
            );
          }}
        />
      </Card>
      <Modal
        title="Ödeme Ekle"
        open={addModalOpen}
        onCancel={() => { setAddModalOpen(false); addForm.resetFields(); setStudentOptions([]); }}
        footer={null}
        destroyOnClose
      >
        <Form form={addForm} layout="vertical" onFinish={handleAddPayment}>
          <Form.Item name="student_id" label="Öğrenci" rules={[{ required: true, message: 'Öğrenci seçiniz.' }]}>
            <Select
              showSearch
              placeholder="İsim ile ara..."
              filterOption={false}
              onSearch={searchStudents}
              loading={studentSearchLoading}
              notFoundContent={studentSearchLoading ? 'Aranıyor...' : studentSearch.length < 2 ? 'En az 2 karakter girin' : 'Öğrenci bulunamadı'}
            >
              {studentOptions.map(s => (
                <Option key={s.id} value={s.id}>{formatStudentLabel(s)}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="amount" label="Tutar (₺)" rules={[{ required: true, message: 'Tutar giriniz.' }]}>
            <InputNumber min={0.01} step={0.01} style={{ width: '100%' }} placeholder="0.00" />
          </Form.Item>

          <Form.Item name="payment_method" label="Ödeme Yöntemi" rules={[{ required: true, message: 'Yöntem seçiniz.' }]}>
            <Select placeholder="Seçiniz">
              <Option value="cash">Nakit</Option>
              <Option value="transfer">Havale/EFT</Option>
              <Option value="credit_card">Kredi Kartı</Option>
              <Option value="other">Diğer</Option>
            </Select>
          </Form.Item>

          <Form.Item name="notes" label="Notlar">
            <Input.TextArea rows={2} placeholder="İsteğe bağlı not..." />
          </Form.Item>

          <Form.Item className="mb-0 flex justify-end">
            <Button onClick={() => { setAddModalOpen(false); addForm.resetFields(); setStudentOptions([]); }} className="mr-2">İptal</Button>
            <Button type="primary" htmlType="submit" loading={addLoading}>Kaydet</Button>
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title="Ödeme Düzenle"
        open={editModalOpen}
        onCancel={() => { setEditModalOpen(false); setEditingPayment(null); }}
        footer={null}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical" onFinish={handleEditPayment}>
          <Form.Item name="amount" label="Tutar (₺)" rules={[{ required: true, message: 'Tutar giriniz.' }]}>
            <InputNumber min={0.01} step={0.01} style={{ width: '100%' }} placeholder="0.00" />
          </Form.Item>

          <Form.Item name="payment_method" label="Ödeme Yöntemi" rules={[{ required: true, message: 'Yöntem seçiniz.' }]}>
            <Select placeholder="Seçiniz">
              <Option value="cash">Nakit</Option>
              <Option value="transfer">Havale/EFT</Option>
              <Option value="credit_card">Kredi Kartı</Option>
              <Option value="other">Diğer</Option>
            </Select>
          </Form.Item>

          <Form.Item name="paid_at" label="Tarih" rules={[{ required: true, message: 'Tarih seçiniz.' }]}>
            <DatePicker showTime format="DD.MM.YYYY HH:mm" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="notes" label="Notlar">
            <Input.TextArea rows={2} placeholder="İsteğe bağlı not..." />
          </Form.Item>

          <Form.Item className="mb-0 flex justify-end">
            <Button onClick={() => { setEditModalOpen(false); setEditingPayment(null); }} className="mr-2">İptal</Button>
            <Button type="primary" htmlType="submit" loading={editLoading}>Kaydet</Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Payments;